import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs, collectionGroup, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { OrderManagementService } from '../../../services/OrderManagementService';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../../styles/ThemeStyles';
import { PDFService } from '../../../services/PDFService';
import { EmailService } from '../../../services/EmailService';
import { getTranslatedProductName } from '../../../utils/translationHelpers';
import { 
  FileText, 
  Mail, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft,
  Send,
  Download,
  Loader
} from 'lucide-react';

const PreviewContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: ${Spacing.xl};
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.lg};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
`;


const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  padding: ${Spacing.lg};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  border-radius: ${BorderRadius.lg};
  color: white;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    gap: ${Spacing.md};
    margin-bottom: ${Spacing.md};
    padding: ${Spacing.md};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.1) rotate(180deg); }
  }
`;

const PreviewHeaderContent = styled.div`
  position: relative;
  z-index: 1;
`;

const PreviewTitle = styled.h2`
  font-size: ${Typography.fontSize.xxl};
  font-family: ${Typography.fontFamily.secondary};
  font-weight: ${Typography.fontWeight.bold};
  color: white;
  margin: 0 0 ${Spacing.sm} 0;
  letter-spacing: -0.02em;
`;

const PreviewSubtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  opacity: 0.9;
`;

const PDFPreviewContainer = styled.div`
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  margin-bottom: ${Spacing.xl};
  overflow: hidden;
`;

const PDFHeader = styled.div`
  background: ${ThemeColors.primary.main};
  color: white;
  padding: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const PDFContent = styled.div`
  padding: ${Spacing.lg};
  background: white;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
`;

const StaticPDFPreview = styled.div`
  padding: ${Spacing.xl};
  background: white;
  font-family: 'Helvetica', 'Arial', sans-serif;
  color: #000;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
    line-height: 1.3;
  }
  
  /* PDF-like styling */
  @media print {
    page-break-after: always;
  }
`;

const PDFPreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.lg};
  padding-bottom: ${Spacing.md};
  border-bottom: 2px solid ${ThemeColors.primary.main};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
    padding-bottom: ${Spacing.sm};
  }
`;

const PDFPreviewLogo = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.main};
`;

const PDFPreviewCompanyInfo = styled.div`
  text-align: right;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const PDFPreviewTitle = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.lg} 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.xl};
    margin: ${Spacing.md} 0;
    line-height: 1.2;
  }
`;

const PDFPreviewSection = styled.div`
  margin-bottom: ${Spacing.xl};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
  }
`;

const PDFPreviewSectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  padding-bottom: ${Spacing.xs};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.md};
    margin-bottom: ${Spacing.sm};
  }
`;

const PDFPreviewTwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.md};
    margin-bottom: ${Spacing.md};
  }
`;

const PDFPreviewInfoItem = styled.div`
  margin-bottom: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.xs};
    font-size: ${Typography.fontSize.xs};
  }
`;

const PDFPreviewInfoLabel = styled.span`
  font-weight: ${Typography.fontWeight.bold};
  margin-right: ${Spacing.xs};
`;

const PDFDateInfo = styled.div`
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.sm};
    font-size: ${Typography.fontSize.xs};
  }
`;

const ProductSectionContainer = styled.div`
  margin-bottom: ${Spacing.lg};
  
  @media (max-width: 768px) {
    margin-bottom: ${Spacing.md};
  }
`;

const ProductSectionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  margin-bottom: ${Spacing.sm};
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.sm};
    margin-bottom: ${Spacing.xs};
  }
`;

const PDFPreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${Spacing.lg};
`;

const PDFPreviewTableHeader = styled.thead`
  background: ${ThemeColors.gray[100]};
  
  th {
    padding: ${Spacing.sm};
    text-align: left;
    font-weight: ${Typography.fontWeight.bold};
    font-size: ${Typography.fontSize.sm};
    border-bottom: 1px solid ${ThemeColors.border.light};
    
    @media (max-width: 768px) {
      padding: ${Spacing.xs} ${Spacing.sm};
      font-size: ${Typography.fontSize.xs};
    }
  }
`;

const PDFPreviewTableBody = styled.tbody`
  tr:nth-child(even) {
    background: ${ThemeColors.gray[50]};
  }
  
  td {
    padding: ${Spacing.sm};
    font-size: ${Typography.fontSize.sm};
    border-bottom: 1px solid ${ThemeColors.border.light};
    
    @media (max-width: 768px) {
      padding: ${Spacing.xs} ${Spacing.sm};
      font-size: ${Typography.fontSize.xs};
      line-height: 1.3;
    }
  }
`;

const PDFPreviewTotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg};
  margin-top: ${Spacing.xl};
  border-top: 2px solid ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
    margin-top: ${Spacing.md};
    font-size: ${Typography.fontSize.md};
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.lg} 0 ${Spacing.md} 0;
  padding-bottom: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:first-child {
    margin-top: 0;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xs};
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: ${ThemeColors.text.primary};
  padding: ${Spacing.sm};
  background: #f8f9fa;
  border-radius: ${BorderRadius.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProductName = styled.span`
  font-size: 14px;
  color: ${ThemeColors.text.primary};
  flex: 1;
`;

const ProductQuantity = styled.span`
  font-size: 13px;
  color: ${ThemeColors.text.secondary};
  margin: 0 ${Spacing.md};
`;

const ProductPrice = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${ThemeColors.text.primary};
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg};
  background: #f8f9fa;
  border-radius: ${BorderRadius.md};
  margin-top: ${Spacing.lg};
  border: 2px solid ${ThemeColors.primary.main};
`;

const TotalLabel = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${ThemeColors.text.primary};
`;

const TotalAmount = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: ${ThemeColors.primary.main};
`;

const EmailSection = styled.div`
  margin-top: ${Spacing.xl};
  padding: ${Spacing.lg};
  background: #f8f9fa;
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const EmailLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const EmailInput = styled.input`
  width: 100%;
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: 14px;
  margin-bottom: ${Spacing.lg};
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px rgba(70, 130, 78, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${Spacing.md};
  justify-content: center;
  margin-top: ${Spacing.xl};
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover {
      background: ${ThemeColors.primary.dark};
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.light};
    
    &:hover {
      background: #f8f9fa;
    }
  `}
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  border-radius: ${BorderRadius.sm};
  margin-bottom: ${Spacing.lg};
  
  ${props => props.type === 'success' ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  ` : `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${Spacing.md};
`;

const LoadingText = styled.p`
  color: ${ThemeColors.text.secondary};
  font-size: 14px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${Spacing.md};
  text-align: center;
`;

const ErrorText = styled.p`
  color: #721c24;
  font-size: 16px;
`;

interface HistoricalOrderPreviewScreenProps {
  storeId: string;
  orderId: string;
  onBack: () => void;
  storeName?: string;
  onNavigate?: (tab: 'store-visit' | 'store-order' | 'store-history') => void;
}

export const HistoricalOrderPreviewScreen: React.FC<HistoricalOrderPreviewScreenProps> = ({ 
  storeId,
  orderId,
  onBack,
  storeName,
  onNavigate
}) => {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Load order data from Firebase OrderHistory (finalized orders)
  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let orderData = null;
        
        // Method 1: Try to load by document ID directly (orderId is the Firestore document ID from collectionGroup query)
        try {
          const orderRef = doc(db, 'Stores', storeId, 'OrderHistory', orderId);
          const orderSnap = await getDoc(orderRef);
          
          if (orderSnap.exists()) {
            const data = orderSnap.data();
            
            // Format timestamp helper will be defined after Method 1
            // For now, just store the data - we'll format it after all methods
            orderData = {
              ...data,
              orderId: data.orderId || orderSnap.id || orderId
            };
            console.log('✅ Loaded order by document ID:', orderId);
          }
        } catch (err) {
          console.log('⚠️ Method 1 failed, trying next method...');
        }
        
        // Format timestamp helper function (reusable)
        const formatTimestamp = (date: Date | Timestamp | string | undefined): string | null => {
          if (!date) return null;
          
          let dateObj: Date;
          
          if (date instanceof Date) {
            dateObj = date;
          } else if (typeof date === 'string') {
            dateObj = new Date(date);
          } else if (date && typeof date === 'object' && 'toDate' in date) {
            dateObj = (date as any).toDate();
          } else {
            return null;
          }
          
          if (isNaN(dateObj.getTime())) {
            return null;
          }
          
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          
          return `${year}${month}${day}${hours}${minutes}`;
        };
        
        // Method 2: If not found, search by orderId field in the store's OrderHistory
        if (!orderData) {
          try {
            const historyRef = collection(db, 'Stores', storeId, 'OrderHistory');
            const q = query(historyRef, where('orderId', '==', orderId));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              const data = doc.data();
              
              const orderDate = data.finalizedAt || data.createdAt || data.updatedAt;
              
              // Ensure orderId is in timestamp format
              let finalOrderId = data.orderId || doc.id || orderId;
              if (!finalOrderId || !/^\d{12}$/.test(finalOrderId)) {
                const timestampId = formatTimestamp(orderDate);
                if (timestampId) {
                  finalOrderId = timestampId;
                }
              }
              
              orderData = {
                ...data,
                orderId: finalOrderId,
                poNumber: data.poNumber || '' // Keep existing poNumber if it exists
              };
              console.log('✅ Found order by orderId field in store:', orderId);
            }
          } catch (err) {
            console.log('⚠️ Method 2 failed, trying next method...');
          }
        }
        
        // Method 3: Search across all stores using collectionGroup (in case storeId is wrong)
        if (!orderData) {
          try {
            const ordersQuery = collectionGroup(db, 'OrderHistory');
            // Try by document ID first
            const allDocsSnapshot = await getDocs(ordersQuery);
            let matchingDoc = allDocsSnapshot.docs.find(doc => doc.id === orderId);
            
            // If not found by document ID, try by orderId field
            if (!matchingDoc) {
              const q = query(ordersQuery, where('orderId', '==', orderId));
              const querySnapshot = await getDocs(q);
              if (!querySnapshot.empty) {
                matchingDoc = querySnapshot.docs[0];
              }
            }
            
            if (matchingDoc) {
              const data = matchingDoc.data();
              // Extract actual storeId from path
              const pathSegments = matchingDoc.ref.path.split('/');
              const actualStoreId = pathSegments[pathSegments.length - 3];
              
              const orderDate = data.finalizedAt || data.createdAt || data.updatedAt;
              
              // Ensure orderId is in timestamp format
              let finalOrderId = data.orderId || matchingDoc.id || orderId;
              if (!finalOrderId || !/^\d{12}$/.test(finalOrderId)) {
                const timestampId = formatTimestamp(orderDate);
                if (timestampId) {
                  finalOrderId = timestampId;
                }
              }
              
              orderData = {
                ...data,
                orderId: finalOrderId,
                poNumber: data.poNumber || '' // Keep existing poNumber if it exists
              };
              console.log('✅ Found order across all stores:', orderId, 'in store:', actualStoreId);
            }
          } catch (err) {
            console.log('⚠️ Method 3 failed');
          }
        }
        
          // Ensure orderId is in timestamp format (YYYYMMDDHHMM)
        if (orderData) {
            const orderDate = orderData.finalizedAt || orderData.createdAt || orderData.updatedAt;
            
            // Check if orderId is already in timestamp format
            if (!orderData.orderId || !/^\d{12}$/.test(orderData.orderId)) {
              // Generate timestamp format from order date
              const timestampId = formatTimestamp(orderDate);
              if (timestampId) {
                orderData.orderId = timestampId;
              }
            }
            
            // Set poNumber for display (keep existing poNumber if it exists, otherwise use orderId)
            if (!orderData.poNumber || orderData.poNumber.trim() === '') {
              orderData.poNumber = orderData.orderId || '';
            }
          
          setOrderData(orderData);
          // Email field starts empty - user must manually enter email address
          setEmail('');
          console.log('✅ Loaded finalized order from OrderHistory:', orderData.orderId);
        } else {
          console.error('❌ Order not found in OrderHistory:', orderId, 'for store:', storeId);
          setError('Order not found');
        }
      } catch (err: any) {
        console.error('Error loading order from OrderHistory:', err);
        setError(err.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    if (storeId && orderId) {
      loadOrder();
    }
  }, [storeId, orderId]);

  // Calculate totals
  const calculateTotal = () => {
    if (!orderData) return 0;
    
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    
    return allItems.reduce((total: number, item: any) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getTotalItems = () => {
    if (!orderData) return 0;
    
    return [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ].reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0);
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter an email address');
      setSubmitStatus('error');
      return;
    }

    if (!email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setSubmitStatus('error');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitStatus('idle');
      setErrorMessage('');
      
      // Generate PDF - pass translation function
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData,
        orderId: orderData.orderId || orderId,
        customerEmail: email,
        t: t // Pass translation function
      });
      
      // Send email - pass translation function
      EmailService.initialize();
      await EmailService.sendOrderConfirmation(email, pdfBlob, orderData.orderId || orderId, orderData, t);
      
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Generate PDF - pass translation function
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData,
        orderId: orderData.orderId || orderId,
        customerEmail: email,
        t: t // Pass translation function
      });
      
      const filename = `order_${orderData.orderId || orderId}.pdf`;
      PDFService.downloadPDF(pdfBlob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Failed to generate PDF');
      setSubmitStatus('error');
    }
  };

  if (loading) {
    return (
      <PreviewContainer>
        <LoadingContainer>
          <Loader size={48} color={ThemeColors.primary.main} className="spin" />
          <LoadingText>{t('orderPreview.loading')}</LoadingText>
        </LoadingContainer>
      </PreviewContainer>
    );
  }

  if (error || !orderData) {
    return (
      <PreviewContainer>
        <ErrorContainer>
          <AlertCircle size={48} color="#721c24" />
          <ErrorText>{error || t('orderHistory.orderNotFound', 'Order not found')}</ErrorText>
          <Button variant="secondary" onClick={() => {
            // Navigate to store history tab if onNavigate is available, otherwise use onBack
            if (onNavigate) {
              onNavigate('store-history');
            } else {
              onBack();
            }
          }}>
            <ArrowLeft size={20} />
            {t('orderPreview.backToHistory')}
          </Button>
        </ErrorContainer>
      </PreviewContainer>
    );
  }

  const totalAmount = calculateTotal();
  const totalItems = getTotalItems();

  return (
    <PreviewContainer>

      {/* Preview Header */}
      <PreviewHeader>
        <FileText size={40} color="white" />
        <PreviewHeaderContent>
          <PreviewTitle>{t('orderPreview.title')}</PreviewTitle>
          <PreviewSubtitle>{t('orderPreview.poNumber')}: {orderData.poNumber || t('pdf.na')}</PreviewSubtitle>
        </PreviewHeaderContent>
      </PreviewHeader>

      <PDFPreviewContainer>
        <PDFHeader>
          <FileText size={20} />
          <span>Order Summary - {orderData.storeName || 'Store Order'}</span>
        </PDFHeader>
        
        <PDFContent>
          <StaticPDFPreview>
            {/* Header with Logo and Company Info */}
            <PDFPreviewHeader>
              <PDFPreviewLogo>MCKENZIE</PDFPreviewLogo>
              <PDFPreviewCompanyInfo>
                <div><strong>McKenzie Seeds</strong></div>
                <div>1000 Parker Blvd</div>
                <div>Brandon, MB R7A 6E1</div>
                <div>Eng/Fr 1-800-665-6340</div>
                <div>Fax 1-888-883-8222</div>
              </PDFPreviewCompanyInfo>
            </PDFPreviewHeader>

            {/* Title */}
            <PDFPreviewTitle>{t('pdf.orderConfirmation')}</PDFPreviewTitle>
            
            <PDFDateInfo>
              <div><strong>{t('pdf.poNumber')}:</strong> {orderData.poNumber || t('pdf.na')}</div>
              <div><strong>{t('pdf.date')}:</strong> {new Date().toLocaleDateString()}</div>
            </PDFDateInfo>

            {/* Customer and Order Information (Two Columns) */}
            <PDFPreviewTwoColumn>
              {/* Customer Information */}
              <PDFPreviewSection>
                <PDFPreviewSectionTitle>{t('pdf.customerInformation')}</PDFPreviewSectionTitle>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.customerHash')}:</PDFPreviewInfoLabel>
                  {orderData.customerNumber || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.storeName')}:</PDFPreviewInfoLabel>
                  {orderData.storeName || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.address')}:</PDFPreviewInfoLabel>
                  {orderData.address || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.city')}:</PDFPreviewInfoLabel>
                  {orderData.city || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.province')}:</PDFPreviewInfoLabel>
                  {orderData.province || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.postalCode')}:</PDFPreviewInfoLabel>
                  {orderData.postalCode || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.email')}:</PDFPreviewInfoLabel>
                  {orderData.email || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.phone')}:</PDFPreviewInfoLabel>
                  {orderData.phoneNumber || t('pdf.na')}
                </PDFPreviewInfoItem>
              </PDFPreviewSection>

              {/* Order Information */}
              <PDFPreviewSection>
                <PDFPreviewSectionTitle>{t('pdf.orderInformation')}</PDFPreviewSectionTitle>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.poNumber')}:</PDFPreviewInfoLabel>
                  {orderData.poNumber || t('pdf.na')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.orderDate')}:</PDFPreviewInfoLabel>
                  {orderData.orderDate || new Date().toLocaleDateString()}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.deliveryDate')}:</PDFPreviewInfoLabel>
                  {orderData.deliveryDate || t('pdf.tbd')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.orderType')}:</PDFPreviewInfoLabel>
                  {orderData.orderType === 'custOrder' ? t('pdf.custOrder') : 
                   orderData.orderType === 'offCarDelivery' ? t('pdf.offCarDelivery') : t('pdf.standardOrder')}
                </PDFPreviewInfoItem>
                <PDFPreviewInfoItem>
                  <PDFPreviewInfoLabel>{t('pdf.specialInstructions')}:</PDFPreviewInfoLabel>
                  {orderData.specialInstructions || t('pdf.none')}
                </PDFPreviewInfoItem>
              </PDFPreviewSection>
            </PDFPreviewTwoColumn>

            {/* Product Selections */}
            <PDFPreviewSection>
              <PDFPreviewSectionTitle>{t('pdf.productSelections')}</PDFPreviewSectionTitle>
              
              {(() => {
                const heritageItems = orderData.heritageItems || [];
                const livingstonItems = orderData.livingstonItems || [];
                const flowerItems = orderData.flowerItems || [];
                const vegetableItems = orderData.vegetableItems || [];
                
                const formSections = [
                  { items: heritageItems, name: t('categories.heritage'), category: 'H' },
                  { items: livingstonItems, name: 'Livingston', category: 'L' },
                  { items: flowerItems, name: t('categories.flower'), category: 'F' },
                  { items: vegetableItems, name: t('categories.vegetable'), category: 'V' }
                ].filter(section => section.items.length > 0);

                if (formSections.length === 0) {
                  return <p style={{ fontStyle: 'italic', color: ThemeColors.text.secondary }}>{t('pdf.noProductsSelected')}</p>;
                }

                return formSections.map((section, sectionIndex) => {
                  const sectionTotal = section.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                  const sectionItemCount = section.items.reduce((sum, item) => sum + item.quantity, 0);
                  const itemLabel = sectionItemCount === 1 ? t('pdf.item') : t('pdf.items');

                  return (
                    <ProductSectionContainer key={sectionIndex}>
                      <ProductSectionTitle>
                        {section.name} {t('pdf.products')}:
                      </ProductSectionTitle>
                      <PDFPreviewTable>
                        <PDFPreviewTableHeader>
                          <tr>
                            <th>{t('pdf.product')}</th>
                            <th>{t('pdf.qty')}</th>
                            <th>{t('pdf.price')}</th>
                            <th>{t('pdf.total')}</th>
                          </tr>
                        </PDFPreviewTableHeader>
                        <PDFPreviewTableBody>
                          {section.items.map((item, itemIndex) => {
                            // Translate product name (stored in English, displayed translated)
                            const translatedProductName = getTranslatedProductName(item.packetDescription || '', t);
                            return (
                              <tr key={itemIndex}>
                                <td>
                                  <span style={{ fontWeight: Typography.fontWeight.bold }}>[{section.category}]</span>{' '}
                                  {translatedProductName}
                                  {item.code && <span style={{ fontSize: '0.85em', color: ThemeColors.text.secondary }}> ({t('pdf.code')}: {item.code})</span>}
                                </td>
                                <td>{item.quantity}</td>
                                <td>${item.price.toFixed(2)}</td>
                                <td>${(item.price * item.quantity).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </PDFPreviewTableBody>
                      </PDFPreviewTable>
                      <div style={{ textAlign: 'right', marginBottom: Spacing.md, fontWeight: Typography.fontWeight.bold }}>
                        {section.name} {t('pdf.subtotal')} ({sectionItemCount} {itemLabel}): ${sectionTotal.toFixed(2)}
                      </div>
                    </ProductSectionContainer>
                  );
                });
              })()}

              {/* Total */}
              <PDFPreviewTotalSection>
                <div>{t('pdf.total')} ({totalItems} {totalItems === 1 ? t('pdf.item') : t('pdf.items')})</div>
                <div>${totalAmount.toFixed(2)}</div>
              </PDFPreviewTotalSection>
            </PDFPreviewSection>
          </StaticPDFPreview>
        </PDFContent>
      </PDFPreviewContainer>

      {/* Email Section */}
      <EmailSection>
        <EmailLabel htmlFor="email">
          <Mail size={16} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
          {t('orderPreview.sendCopyToEmail')}
        </EmailLabel>
        <EmailInput
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('orderPreview.enterEmail')}
        />
        <p style={{ fontSize: '12px', color: ThemeColors.text.secondary, margin: 0 }}>
          {t('orderPreview.emailCopyNote')}
        </p>
      </EmailSection>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <StatusMessage type="success">
          <CheckCircle size={20} />
          {t('orderPreview.emailSent')}
        </StatusMessage>
      )}

      {submitStatus === 'error' && (
        <StatusMessage type="error">
          <AlertCircle size={20} />
          {errorMessage}
        </StatusMessage>
      )}

      {/* Action Buttons */}
      <ButtonContainer>
        <Button variant="secondary" onClick={() => {
          // Navigate to store history tab if onNavigate is available, otherwise use onBack
          if (onNavigate) {
            onNavigate('store-history');
          } else {
            onBack();
          }
        }}>
          <ArrowLeft size={20} />
          {t('orderPreview.backToHistory')}
        </Button>
        
        <Button variant="secondary" onClick={handleDownloadPDF}>
          <Download size={20} />
          {t('orderPreview.downloadPDF')}
        </Button>
        
        <Button 
          variant="primary" 
          onClick={handleSendEmail}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader size={20} className="spin" />
              {t('orderPreview.sending')}
            </>
          ) : (
            <>
              <Send size={20} />
              {t('orderPreview.sendEmail')}
            </>
          )}
        </Button>
      </ButtonContainer>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </PreviewContainer>
  );
};

